import type { PostHogEvent } from "common/contexts/PostHogAnalyticsProvider";
import { FrequencyUnit } from "common/types";
import { FreeShippingStatus } from "common/types/carts";
import { PromotionKind } from "common/types/promotions";
import { Satisfies } from "common/utils/types";

import type { PersonalizationKind, ProductListSort } from "client/products/types";
import {
  ClientPasswordResetRequestErrorKind,
  ClientSignupErrorKind,
  LoginErrorKind,
  SignupVerificationCodeErrorKind,
} from "client/accounts/types";

/**
 * GA4 ecommerce event names sent via gtag. Partners use these to trigger events in their
 * Google Tag Manager setup. Ensure these names are kept consistent with the documentation
 * we've shared with them here:
 * https://docs.google.com/document/d/1sYGNv_llIEIvoFTq2Z9fX12oGn3BT46NgAlDYM0LsI0/
 */
export const EcommerceEventName = {
  Purchase: "purchase",
  AddToCart: "add_to_cart",
  RemoveFromCart: "remove_from_cart",
  ViewItem: "view_item",
  SelectItem: "select_item",
  ViewItemList: "view_item_list",
  BeginCheckout: "begin_checkout",
  AddShippingInfo: "add_shipping_info",
  AddPaymentInfo: "add_payment_info",
} as const;
export type EcommerceEventName = (typeof EcommerceEventName)[keyof typeof EcommerceEventName];

/**
 * Custom (non-GA4) analytics event names pushed directly to the dataLayer.
 * Each event gets a `vetcove_` prefix when fired (e.g. `vetcove_page_view`) to
 * namespace it away from GA4 reserved names and any other tags in the container.
 */
export const CustomAnalyticsEventName = {
  PageView: "page_view",
} as const;
export type CustomAnalyticsEventName =
  (typeof CustomAnalyticsEventName)[keyof typeof CustomAnalyticsEventName];

export const ClientPostHogEventName = {
  AddToCart: "add_to_cart",
  AddToCartFromLink: "add_to_cart_from_link",
  BeginCheckout: "begin_checkout",
  CheckoutCompleted: "checkout_completed",
  FeatureFlagCalled: "$feature_flag_called",
  FreeShippingProgressView: "free_shipping_progress_view",
  ItemClick: "item_click",
  ProductClick: "product_click",
  ProductLineClick: "product_line_click",
  ProductLineView: "product_line_view",
  ProductListFilterChange: "product_list_filter_change",
  ProductListSortChange: "product_list_sort_change",
  ProductLinesView: "product_lines_view",
  ProductsView: "products_view",
  PromotionInfoClick: "promotion_info_click",
  RemoveFromCart: "remove_from_cart",
  UpdateCartItemAutoship: "update_cart_item_autoship",
  UpdatePaymentMethod: "update_payment_method",
  UpdateShippingMethod: "update_shipping_method",
  LoginAttempted: "login_attempted",
  LoginFailed: "login_failed",
  LoginSucceeded: "login_succeeded",
  PasswordResetRequested: "password_reset_requested",
  PasswordResetRequestSucceeded: "password_reset_request_succeeded",
  PasswordResetRequestFailed: "password_reset_request_failed",
  SignupCompleted: "signup_completed",
  SignupFailed: "signup_failed",
  SignupCodeRequested: "signup_code_requested",
  SignupCodeRequestSucceeded: "signup_code_request_succeeded",
  SignupCodeRequestFailed: "signup_code_request_failed",
} as const;
export type ClientPostHogEventName =
  (typeof ClientPostHogEventName)[keyof typeof ClientPostHogEventName];

export type AuthUsernameType = "email" | "phone";

/**
 * Flat item shape sent to GTM. Matches the GA4 ecommerce item schema.
 * Standard GA4 ecommerce item fields.
 * @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events#view_item_list_item
 */
type GA4EcommerceItemFields = {
  item_id?: string;
  item_name?: string;
  item_brand?: string;
  item_category?: string;
  /** Comma-separated list of non-primary category names on the product line. */
  item_category2?: string;
  price?: number;
  discount?: number;
  quantity?: number;
  index?: number;
};

/** Custom Vetcove fields added to ecommerce items for V2 events. */
type VetcoveEcommerceItemFields = {
  vetcove_item_id?: string;
  vetcove_item_name?: string;
  product_id?: string;
  product_name?: string;
  prescription_required?: boolean;
  manufacturer_name?: string;
  days_supply?: number;
  frequency?: string;
};

export type EcommerceItem = GA4EcommerceItemFields & VetcoveEcommerceItemFields;

/**
 * A generic type for an Ecommerce event we send to google tag manager.
 * Generally these follow the format of GA4 ecommerce events.
 *
 * Includes a *name* that corresponds to one of the events found at
 * https://developers.google.com/analytics/devguides/collection/ga4/reference/events
 * and the related *data* we will send alongwith it
 */
export type EcommerceEventType<T extends EcommerceEventName, E extends object> = {
  name: T;
  data: E;
};

/**
 * Generic type for a custom (non-GA4) analytics event pushed to the dataLayer.
 * The *name* is a value from CustomAnalyticsEventName; sendDataLayerEvent
 * prefixes it with `vetcove_` before dispatch.
 */
export type CustomAnalyticsEventType<T extends CustomAnalyticsEventName, E extends object> = {
  name: T;
  data: E;
};

/** PostHog experiment flag keys. Must match marketing.constants.POSTHOG_EXPERIMENT_KEY_BY_FEATURE. */
export const Experiment = {
  ProductCardAddToCart: "product-card-add-to-cart",
} as const;
export type Experiment = (typeof Experiment)[keyof typeof Experiment];

/** @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events#purchase */
export type EcommercePurchaseEvent = EcommerceEventType<
  typeof EcommerceEventName.Purchase,
  {
    currency: "USD";
    /** Some unique identifier to avoid duplicate purchase events */
    transaction_id: string;
    /** Dollar value of the transaction */
    value: number;
    shipping: number;
    tax: number;
    items: EcommerceItem[];
    coupon?: string;
  }
>;

/** @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events#add_to_cart */
export type EcommerceAddToCartEvent = EcommerceEventType<
  typeof EcommerceEventName.AddToCart,
  {
    currency: "USD";
    value: number;
    /** The set of items added to the cart */
    items: EcommerceItem[];
  }
>;

/** @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events#remove_from_cart */
export type EcommerceRemoveFromCartEvent = EcommerceEventType<
  typeof EcommerceEventName.RemoveFromCart,
  {
    currency: "USD";
    value: number;
    /** The set of items removed from the cart */
    items: EcommerceItem[];
  }
>;

/** @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events#view_item */
export type EcommerceViewItemEvent = EcommerceEventType<
  typeof EcommerceEventName.ViewItem,
  {
    currency: "USD";
    value: number;
    /** The set of items viewed */
    items: EcommerceItem[];
  }
>;

/** @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events#select_item */
export type EcommerceSelectItemEvent = EcommerceEventType<
  typeof EcommerceEventName.SelectItem,
  {
    /* The name of the list the item is viewed in */
    item_list_name: string;
    /** The set of items selected from the list */
    items: EcommerceItem[];
  }
>;

/** @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events#view_item_list */
export type EcommerceViewItemListEvent = EcommerceEventType<
  typeof EcommerceEventName.ViewItemList,
  {
    /* The name of the list the item is viewed in */
    item_list_name: string;
    /** The set of items in the list */
    items: EcommerceItem[];
  }
>;

/** @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events#begin_checkout */
export type EcommerceBeginCheckoutEvent = EcommerceEventType<
  typeof EcommerceEventName.BeginCheckout,
  {
    currency: "USD";
    value: number;
    items: EcommerceItem[];
  }
>;

/** @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events#add_shipping_info */
export type EcommerceAddShippingInfoEvent = EcommerceEventType<
  typeof EcommerceEventName.AddShippingInfo,
  {
    currency: "USD";
    value: number;
    coupon?: string;
    shipping_tier: string;
    items: EcommerceItem[];
  }
>;

export type EcommercePaymentType = "klarna" | "apple_pay" | "google_pay" | "credit_card";

/** @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events#add_payment_info */
export type EcommerceAddPaymentInfoEvent = EcommerceEventType<
  typeof EcommerceEventName.AddPaymentInfo,
  {
    currency: "USD";
    value: number;
    coupon?: string;
    payment_type: EcommercePaymentType;
    items: EcommerceItem[];
  }
>;

/**
 * page_view event for SPA virtual pageview tracking.
 * Fires on every page load and client-side route change.
 *
 * Standard GA4 parameters: page_location, page_referrer, page_title
 * Custom parameters: page_name, page_type
 *
 * @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events#page_view
 */
export type PageViewData = {
  /** Full URL (e.g., https://example.com/pet/products/123) — standard GA4 parameter */
  page_location: string;
  /** Previous URL — document.referrer on initial load, previous internal URL for SPA navigations — standard GA4 parameter */
  page_referrer: string;
  /** document.title, falling back to pathname — standard GA4 parameter */
  page_title: string;
  /** Unique page identifier (e.g., "product-line", "checkout-items") — custom */
  page_name: string;
  /** Feature area / module (e.g., "products", "checkout") — custom */
  page_type: string;
};

export type PageViewEvent = CustomAnalyticsEventType<
  typeof CustomAnalyticsEventName.PageView,
  PageViewData
>;

/** Where an add-to-cart happened when it wasn't on a product list (see ProductListSource). */
export const AddToCartPageSource = {
  ProductDetailPage: "Product Detail Page",
  Checkout: "Checkout",
  CartItem: "Cart Item",
  Subscription: "Subscription",
  OrderItem: "Order Item",
  Prescription: "Prescription",
} as const;
export type AddToCartPageSource = (typeof AddToCartPageSource)[keyof typeof AddToCartPageSource];

/** Shares strings with product_click / products_view so one `source` filter follows list → click → add. */
export type AddToCartSource = ProductListSource | AddToCartPageSource;

export const ProductLineListSource = {
  HomePageFeatured: "Home Page - Featured",
  HomePageForYou: "Home Page - For You",
  HomePageCategory: "Home Page - Category",
  BrowseCatalogFeatured: "Browse Catalog - Featured",
  BrowseCatalogForYou: "Browse Catalog - For You",
  BrowseCatalogCategory: "Browse Catalog - Category",
  MyZoetisRewards: "My Zoetis Rewards",
  CategoryPageProductLines: "Category Page - Product Lines",
} as const;
export type ProductLineListSource =
  (typeof ProductLineListSource)[keyof typeof ProductLineListSource];

/** Where on a product list the shopper changed a filter. */
export const ProductListFilterSurface = {
  /** The tier-2 category pills on a tier-1 category page. */
  Subcategories: "subcategories",
  /** The filter panel beside the results. */
  Rail: "rail",
  /** The filter panel in a modal on narrow viewports, sent when the shopper applies it. */
  Drawer: "drawer",
  /** The removable chips for the selected values, and their Clear all. */
  AppliedFilters: "applied_filters",
} as const;
export type ProductListFilterSurface =
  (typeof ProductListFilterSurface)[keyof typeof ProductListFilterSurface];

/** Which list an event happened on: the search, collection, category or promotion behind it. */
export type ProductListIdentity = {
  search_term?: string;
  collection_slug?: string;
  category_id?: number;
  promotion_id?: number;
};

export const ProductListSource = {
  SearchResults: "Search Results",
  DealsPage: "Deals Page",
  BrandsPage: "Brands Page",
  DealsCollectionPage: "Deals Collection Page",
  CategoryPage: "Category Page",
  RebateDrawer: "Rebate Drawer",
  IncentiveDrawer: "Incentive Drawer",
  RebatePage: "Rebate Page",
  IncentivePage: "Incentive Page",
} as const;
export type ProductListSource = (typeof ProductListSource)[keyof typeof ProductListSource];

export const ItemClickSource = {
  Prescription: "Prescription",
  Subscription: "Subscription",
  CartItem: "Cart Item",
  OrderItem: "Order Item",
  OrderSupport: "Order Support",
  OrderItemModification: "Order Item Modification",
  CrossSell: "Cross Sell",
} as const;
export type ItemClickSource = (typeof ItemClickSource)[keyof typeof ItemClickSource];

/** Where the free shipping progress banner rendered */
export const FreeShippingProgressSurface = {
  CartDrawer: "Cart Drawer",
  CheckoutSummary: "Checkout Summary",
  ItemOptionsSummary: "Item Options Summary",
  ReviewOrderSummary: "Review Order Summary",
} as const;
export type FreeShippingProgressSurface =
  (typeof FreeShippingProgressSurface)[keyof typeof FreeShippingProgressSurface];

export type CustomAnalyticsEvent = PageViewEvent;

/** PostHog's default experiment exposure event. @see https://posthog.com/docs/experiments/exposures */
export type FeatureFlagCalledEventData = {
  $feature_flag: Experiment;
  $feature_flag_response: string;
};

export type CheckoutCompletedEventData = {
  id: number;
  cart_id: number;
  client_id: number;
  clinic_id: number;
  payment_method_id: number | null;
  shipping_address_id: number | null;
  total: number;
  discounted_subtotal: number;
  tax: number;
  shipping: number;
  promo_code_ids: number[];
  order_items: {
    id: number;
    item_id: number;
    patient_id: number | null;
    prescription_fill_id: number | null;
    quantity: number;
    unit_price: number;
    amortized_discount: number;
    status: string | null;
  }[];
};

export type AddToCartEventData = {
  source: AddToCartSource;
  cart_id: number | null;
  cart_item_id: number;
  item_id: number;
  product_id: number;
  product_line_id: number;
  patient_id: number | null;
  flavor_selection_id: number | null;
  quantity: number;
  unit_price: number;
  discount: number | null;
  /** Same name as the backend `order_item_placed` property, so one filter spans add and order. */
  is_autoship: boolean;
  /** The cart line's schedule after the add, null when it is one-time. */
  frequency: number | null;
  interval_unit: FrequencyUnit | null;
  /** The collection page the add happened on. */
  collection_slug?: string;
  category_id?: number;
};

export type AddToCartFromLinkEventData = {
  cart_id: number | null;
  cart_item_id: number;
  item_id: number;
  patient_id: number | null;
  quantity: number;
};

export type RemoveFromCartEventData = {
  cart_id: number;
  cart_item_id: number;
  item_id: number;
  patient_id: number | null;
  flavor_selection_id: number | null;
  quantity: number;
  unit_price: number;
  discount: number;
};

export type UpdateCartItemAutoshipEventData = {
  cart_id: number;
  cart_item_id: number;
  item_id: number;
  is_enabled: boolean;
  /** The chosen schedule, null when Autoship is being turned off. */
  frequency: number | null;
  interval_unit: FrequencyUnit | null;
};

export type ProductLineViewEventData = {
  id: number;
  name: string;
  category_ids: number[];
  prescription_required: boolean;
  is_compound: boolean;
  product_id: number;
  item_id: number;
  standard_price: number;
  autoship_price: number;
};

export type ProductLineClickEventData = {
  source: ProductLineListSource;
  id: number;
  category_id: number | null;
  personalization_kind: PersonalizationKind | null;
  /** Zero-based slot in the row it was clicked from. */
  position?: number;
};

/** A shopper selecting or clearing one filter value on a product list. */
export type ProductListFilterChangeEventData = ProductListIdentity & {
  source: ProductListSource;
  /** The filter's key, e.g. "category". */
  filter: string;
  value: string | number | boolean;
  selected: boolean;
  /** The filter's selected values after the change. */
  selected_values: (string | number | boolean)[];
  surface: ProductListFilterSurface;
  /** Zero-based slot in the surface, when it is a row. */
  position?: number;
};

export type ProductListSortChangeEventData = ProductListIdentity & {
  source: ProductListSource;
  sort: ProductListSort;
  previous_sort: ProductListSort;
};

export type ProductClickEventData = {
  source: ProductListSource;
  id: number;
  product_line_id: number;
  default_item_id?: number;
  /** Which collection the card was clicked on; `source` alone cannot say once more than
   * one collection page exists. */
  collection_slug?: string;
  category_id?: number;
};

export type ItemClickEventData = {
  source: ItemClickSource;
  id: number;
  product_id: number;
  product_line_id: number;
  unit_price: number | null;
};

/** A shopper opening a promotion's details from a card that names it. */
export type PromotionInfoClickEventData = {
  source: ProductListSource;
  kind: PromotionKind;
  promotion_id: number;
  collection_slug?: string;
  /** Zero-based slot in the row it was clicked from. */
  position?: number;
};

export type ProductsViewEventData = ProductListIdentity & {
  source: ProductListSource;
  product_ids: number[];
  /** Total matches, not the page size. */
  num_results?: number;
  /** Search request time in ms, measured in the browser. Absent for results served from cache. */
  latency_ms?: number;
  sort?: ProductListSort;
};

export type ProductLinesViewEventData = {
  source: ProductLineListSource;
  product_line_ids: number[];
  personalization_kinds: (PersonalizationKind | null)[];
  category_id: number | null;
};

export type FreeShippingProgressViewEventData = {
  cart_id: number;
  surface: FreeShippingProgressSurface;
  status: FreeShippingStatus;
  amount_remaining: number;
  paid_shipment_count: number;
};

export type BeginCheckoutEventData = {
  cart_id: number;
  client_id: number;
  clinic_id: number;
  subtotal: number;
  discount: number;
  shipping: number;
  total: number;
  promo_code_ids: number[];
  // Both free shipping fields: sent whether or not the banner flag is on, so both experiment
  // arms compare; null when the cart has no free shipping progress
  free_shipping_status: FreeShippingStatus | null;
  free_shipping_amount_remaining: number | null;
  cart_items: {
    id: number;
    item_id: number;
    patient_id: number | null;
    flavor_selection_id: number | null;
    quantity: number;
    unit_price: number;
    discount: number;
  }[];
};

export type UpdateShippingMethodEventData = {
  cart_id: number;
  client_id: number;
  clinic_id: number;
  shipping_address_id: number | null;
  vendor_slug: string | null;
  shipping_method_id: number;
  shipping_method_title: string;
  shipping_cost: number;
  cart_item_ids: number[];
};

export type UpdatePaymentMethodEventData = {
  cart_id: number;
  client_id: number;
  clinic_id: number;
  payment_method_id: number | null;
  payment_type: string;
  wallet_type: string | null;
  card_brand: string | null;
};

export type LoginAttemptedEventData = {
  username: string;
};

export type LoginFailedEventData = {
  username: string;
  error_category: LoginErrorKind | null;
  error_message: string | null;
};

export type LoginSucceededEventData = {
  username: string;
};

export type PasswordResetRequestedEventData = {
  username: string;
};

export type PasswordResetRequestSucceededEventData = {
  username: string;
};

export type PasswordResetRequestFailedEventData = {
  username: string;
  error_category: ClientPasswordResetRequestErrorKind | null;
  error_message: string | null;
};

export type SignupCompletedEventData = {
  email: string;
  phone: string;
  first_name: string;
  last_name: string;
  username_type: AuthUsernameType;
};

export type SignupFailedEventData = {
  email: string;
  phone: string;
  first_name: string;
  last_name: string;
  username_type: AuthUsernameType;
  error_category: ClientSignupErrorKind | null;
  error_message: string | null;
};

export type SignupCodeRequestedEventData = {
  username_type: AuthUsernameType;
  is_resend: boolean;
};

export type SignupCodeRequestSucceededEventData = {
  username_type: AuthUsernameType;
  is_resend: boolean;
};

export type SignupCodeRequestFailedEventData = {
  username_type: AuthUsernameType;
  is_resend: boolean;
  error_category: SignupVerificationCodeErrorKind | null;
  error_message: string | null;
};

/** The data each client PostHog event sends */
type ClientPostHogEventData = Satisfies<
  Record<ClientPostHogEventName, object>,
  {
    [ClientPostHogEventName.FeatureFlagCalled]: FeatureFlagCalledEventData;
    [ClientPostHogEventName.CheckoutCompleted]: CheckoutCompletedEventData;
    [ClientPostHogEventName.AddToCart]: AddToCartEventData;
    [ClientPostHogEventName.AddToCartFromLink]: AddToCartFromLinkEventData;
    [ClientPostHogEventName.RemoveFromCart]: RemoveFromCartEventData;
    [ClientPostHogEventName.UpdateCartItemAutoship]: UpdateCartItemAutoshipEventData;
    [ClientPostHogEventName.ProductLineView]: ProductLineViewEventData;
    [ClientPostHogEventName.ProductLineClick]: ProductLineClickEventData;
    [ClientPostHogEventName.ProductListFilterChange]: ProductListFilterChangeEventData;
    [ClientPostHogEventName.ProductListSortChange]: ProductListSortChangeEventData;
    [ClientPostHogEventName.ProductClick]: ProductClickEventData;
    [ClientPostHogEventName.ItemClick]: ItemClickEventData;
    [ClientPostHogEventName.PromotionInfoClick]: PromotionInfoClickEventData;
    [ClientPostHogEventName.ProductsView]: ProductsViewEventData;
    [ClientPostHogEventName.ProductLinesView]: ProductLinesViewEventData;
    [ClientPostHogEventName.FreeShippingProgressView]: FreeShippingProgressViewEventData;
    [ClientPostHogEventName.BeginCheckout]: BeginCheckoutEventData;
    [ClientPostHogEventName.UpdateShippingMethod]: UpdateShippingMethodEventData;
    [ClientPostHogEventName.UpdatePaymentMethod]: UpdatePaymentMethodEventData;
    [ClientPostHogEventName.LoginAttempted]: LoginAttemptedEventData;
    [ClientPostHogEventName.LoginFailed]: LoginFailedEventData;
    [ClientPostHogEventName.LoginSucceeded]: LoginSucceededEventData;
    [ClientPostHogEventName.PasswordResetRequested]: PasswordResetRequestedEventData;
    [ClientPostHogEventName.PasswordResetRequestSucceeded]: PasswordResetRequestSucceededEventData;
    [ClientPostHogEventName.PasswordResetRequestFailed]: PasswordResetRequestFailedEventData;
    [ClientPostHogEventName.SignupCompleted]: SignupCompletedEventData;
    [ClientPostHogEventName.SignupFailed]: SignupFailedEventData;
    [ClientPostHogEventName.SignupCodeRequested]: SignupCodeRequestedEventData;
    [ClientPostHogEventName.SignupCodeRequestSucceeded]: SignupCodeRequestSucceededEventData;
    [ClientPostHogEventName.SignupCodeRequestFailed]: SignupCodeRequestFailedEventData;
  }
>;

/** Every client PostHog event, discriminated by `name` so `data` is checked against it */
export type ClientPostHogEvent = {
  [TName in ClientPostHogEventName]: PostHogEvent<TName, ClientPostHogEventData[TName]>;
}[ClientPostHogEventName];

export type EcommerceEvent =
  | EcommercePurchaseEvent
  | EcommerceAddToCartEvent
  | EcommerceRemoveFromCartEvent
  | EcommerceViewItemEvent
  | EcommerceSelectItemEvent
  | EcommerceViewItemListEvent
  | EcommerceBeginCheckoutEvent
  | EcommerceAddShippingInfoEvent
  | EcommerceAddPaymentInfoEvent;

/**
 * Product line level data for analytics events.
 *
 * Uses structural typing so domain objects (IItemProductLine, etc.)
 * can be passed directly without remapping.
 *
 * `manufacturer` is only on product search results (per NVA; other views intentionally skip it).
 */
export type AnalyticsProductLineData = {
  id: number;
  name: string;
  brand_name: string;
  prescription_required: boolean;
  primary_category_name: string;
  categories: { name: string }[];
  manufacturer?: { id: number; name: string };
};

/** Product level data for analytics events (includes nested product line). */
export type AnalyticsProductData = {
  id: number;
  name: string;
  product_line: AnalyticsProductLineData;
};

export const isAnalyticsItemData = (data: AnalyticsItemEventData): data is AnalyticsItemData =>
  "product" in data;

export const isAnalyticsProductData = (
  data: AnalyticsItemEventData,
): data is AnalyticsProductData => "product_line" in data;

/** Item level data for analytics events (includes nested product and product line). */
export type AnalyticsItemData = {
  id: number;
  name: string;
  product: AnalyticsProductData;
  days_supply: number;
  duration_value: number;
  duration_unit: string;
};

/**
 * Union of all item event data levels. Used by sendViewItemList, sendSelectItem and the
 * other item events, and through `AnalyticsCartItem.item` by the cart events.
 *
 * Pass whichever level your domain object provides — the deeper the data, the
 * richer the GTM event, but all levels produce valid EcommerceItems.
 *
 * Discriminated at runtime via 'product' in data / 'product_line' in data.
 */
export type AnalyticsItemEventData =
  | AnalyticsProductLineData
  | AnalyticsProductData
  | AnalyticsItemData;

/**
 * Cart item for analytics events. Used by sendAddToCart, sendRemoveFromCart.
 * Consumers provide pre-computed price and discount.
 *
 * ICartItem consumers use getCartItemPricing() to resolve pricing.
 * Non-cart consumers (orders, subscriptions, prescriptions) pass their
 * own computed pricing directly.
 */
export type AnalyticsCartItem = {
  item: AnalyticsItemEventData;
  quantity: number;
  price: number;
  discount: number;
};
